import React, { useState, useRef, useEffect } from 'react';
import {
  ArrowDownTrayIcon,
  DocumentTextIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';

export interface ExportDropdownMenuProps {
  onExport: (format: 'word' | 'pdf' | 'text') => void;
  onPrint?: () => void;
  disabled?: boolean;
  showPrint?: boolean;
  className?: string;
}

export const ExportDropdownMenu: React.FC<ExportDropdownMenuProps> = ({
  onExport,
  onPrint,
  disabled = false,
  showPrint = true,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleExport = (format: 'word' | 'pdf' | 'text') => {
    onExport(format);
    setIsOpen(false);
  };

  const handlePrint = () => {
    if (onPrint) {
      onPrint();
    }
    setIsOpen(false);
  };

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Export"
      >
        <ArrowDownTrayIcon className="h-5 w-5" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          ref={menuRef}
          className="absolute right-0 mt-1 w-44 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50"
        >
          {/* Export Word */}
          <button
            onClick={() => handleExport('word')}
            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <DocumentTextIcon className="h-4 w-4 mr-3 text-blue-600 dark:text-blue-400" />
            Export Word
          </button>

          {/* Export PDF */}
          <button
            onClick={() => handleExport('pdf')}
            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <DocumentTextIcon className="h-4 w-4 mr-3 text-red-600 dark:text-red-400" />
            Export PDF
          </button>

          {/* Export Text */}
          <button
            onClick={() => handleExport('text')}
            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <DocumentTextIcon className="h-4 w-4 mr-3 text-gray-600 dark:text-gray-400" />
            Export Text
          </button>

          {/* Print Option */}
          {showPrint && onPrint && (
            <>
              <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
              <button
                onClick={handlePrint}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <PrinterIcon className="h-4 w-4 mr-3 text-gray-600 dark:text-gray-400" />
                Print
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ExportDropdownMenu;
